/**
 * 검색 파사드 스토어(searchStore)
 * --------------------------------------------------------
 * 이 모듈은 검색 기능에 필요한 모든 하위 스토어(query, filter, result 등)를
 * 하나의 파사드로 묶어 제공합니다.
 * 1) 상태 흐름 추상화
 * 2) 캐시 및 API 호출 관리
 * 3) 뷰, 페이징, 태그, 필터 동기화
 */

// ------------------------------ 상수 · 재내보내기 ------------------------------
export { DEFAULT_PAGE_SIZE } from '../constants';

// 하위 스토어 임포트
import { searchQueryStore } from './query/searchQueryStore';
import { searchFilterStore } from './filter/searchFilterStore';
import { searchCategoryStore } from './category/searchCategoryStore';
import { searchResultStore } from './result/searchResultStore';
import { searchViewStore } from './view/searchViewStore';
import { searchStatusStore } from './status/searchStatusStore';
import { searchHistoryStore } from './history/searchHistoryStore';
import { derived } from 'svelte/store';
import type { SearchResultWithView } from '../../utils/searchResultBucket';

// 원래의 스토어와 호환되는 인터페이스를 위한 구독 함수
// 필요한 모든 스토어의 상태를 하나로 합쳐서 내보냅니다
const createCombinedSubscribe = () => {
    return function subscribe(run: (value: any) => void) {
        // 개별 스토어 구독 설정
        const queryUnsub = searchQueryStore.subscribe(() => update());
        const filterUnsub = searchFilterStore.subscribe(() => update());
        const categoryUnsub = searchCategoryStore.subscribe(() => update());
        const resultUnsub = searchResultStore.subscribe(() => update());
        const viewUnsub = searchViewStore.subscribe(() => update());
        const statusUnsub = searchStatusStore.subscribe(() => update());
        const historyUnsub = searchHistoryStore.subscribe(() => update());

        // 모든 스토어 상태를 합친 객체 생성 및 콜백 호출
        function update() {
            const queryState = searchQueryStore.getState();
            const filterState = searchFilterStore.getState();
            const categoryState = searchCategoryStore.getState();
            const resultState = searchResultStore.getState();
            const viewState = searchViewStore.getState();
            const statusState = searchStatusStore.getState();
            const historyState = searchHistoryStore.getState();

            // 합쳐진 상태
            const combinedState = {
                // 쿼리 스토어
                searchQuery: queryState.searchQuery,

                // 상태 스토어
                isLoadingGlobal: statusState.isLoadingGlobal,
                isLoadingTabData: statusState.isLoadingTabData,
                isLoadingInitialMount: statusState.isLoadingInitialMount,

                // 필터 스토어
                selectedCountry: filterState.selectedCountry,
                selectedServiceTypes: filterState.selectedServiceTypes,
                pendingSelectedCountry: filterState.pendingSelectedCountry,
                pendingSelectedServiceTypes: filterState.pendingSelectedServiceTypes,
                previousSelectedCountryValue: filterState.previousSelectedCountryValue,
                previousSelectedServiceTypesJson: filterState.previousSelectedServiceTypesJson,

                // 카테고리 스토어
                filteredCategories: categoryState.filteredCategories,
                allCategoriesFromInitialData: categoryState.allCategoriesFromInitialData,
                selectedTagsByTab: categoryState.selectedTagsByTab,
                categoryTotalsMap: categoryState.categoryTotalsMap,
                tabCounts: categoryState.tabCounts,
                pendingTabCodes: categoryState.pendingTabCodes,

                // 결과 스토어
                searchResults: resultState.searchResults,
                searchCache: resultState.searchCache,

                // 뷰 스토어
                selectedViewCode: viewState.selectedViewCode,
                currentPage: viewState.currentPage,

                // 검색 히스토리 스토어
                searchHistories: historyState,
            };

            run(combinedState);
        }

        // 초기 업데이트 실행
        update();

        // 정리 함수 반환
        return function () {
            queryUnsub();
            filterUnsub();
            categoryUnsub();
            resultUnsub();
            viewUnsub();
            statusUnsub();
            historyUnsub();
        };
    };
};

// 검색 실행 가능 여부 결정하는 파생
export const canSubmitSearch = derived([searchQueryStore, searchCategoryStore], ([$query, $category]) => {
    // 검색어가 비어있지 않고 필터링된 카테고리가 존재할 때만 검색 가능
    return $query.searchQuery.trim() !== '' && $category.filteredCategories.length > 0;
});

// CombinedState 인터페이스 정의
export interface CombinedState {
    // 쿼리 스토어
    searchQuery: string;

    // 상태 스토어
    isLoadingGlobal: boolean;
    isLoadingTabData: boolean;
    isLoadingInitialMount: boolean;

    // 필터 스토어
    selectedCountry: { value: string; label: string; icon?: string } | null;
    selectedServiceTypes: Array<'WORKSPACE' | 'GENERAL' | 'WEB'>;
    pendingSelectedCountry: { value: string; label: string; icon?: string } | null;
    pendingSelectedServiceTypes: Array<'WORKSPACE' | 'GENERAL' | 'WEB'> | null;
    previousSelectedCountryValue: string | null;
    previousSelectedServiceTypesJson: string;

    // 카테고리 스토어
    filteredCategories: any[]; // 구체적인 타입이 있다면 교체 권장
    allCategoriesFromInitialData: any[];
    selectedTagsByTab: Map<string, any[]>;
    categoryTotalsMap: Map<string, number>;
    tabCounts: Map<string, number>;
    pendingTabCodes: Set<string>;

    // 결과 스토어
    searchResults: SearchResultWithView[];
    searchCache: Map<string, any>;

    // 뷰 스토어
    selectedViewCode: string;
    currentPage: number;

    // 검색 히스토리 스토어
    searchHistories: any[];
}

// ------------------------------ 파사드 스토어 정의 ------------------------------
export const searchStore = {
    // Svelte 스토어 인터페이스
    subscribe: createCombinedSubscribe(),

    set: (value: CombinedState) => {
        searchQueryStore.setSearchQuery(value.searchQuery);
        searchStatusStore.setIsLoadingGlobal(value.isLoadingGlobal);
        searchStatusStore.setIsLoadingTabData(value.isLoadingTabData);
        searchStatusStore.setIsLoadingInitialMount(value.isLoadingInitialMount);
        searchFilterStore.setPendingSelectedCountry(value.pendingSelectedCountry);
        searchFilterStore.setPendingSelectedServiceTypes(value.pendingSelectedServiceTypes || []);
        searchCategoryStore.setAllCategoriesFromInitialData(value.allCategoriesFromInitialData);
        searchResultStore.setSearchResults(value.searchResults);
        searchViewStore.setSelectedViewCode(value.selectedViewCode);
    },

    // 쿼리 스토어
    getQuery: searchQueryStore.getState,
    setSearchQuery: searchQueryStore.setSearchQuery,

    // 필터 스토어
    getFilter: searchFilterStore.getState,
    updateFilter: searchFilterStore.update,
    setPendingSelectedCountry: searchFilterStore.setPendingSelectedCountry,
    setPendingSelectedServiceTypes: searchFilterStore.setPendingSelectedServiceTypes,
    confirmPendingFilters: searchFilterStore.confirmPendingFilters,
    checkFiltersChanged: searchFilterStore.checkFiltersChanged,

    // 카테고리 스토어
    getCategory: searchCategoryStore.getState,
    updateCategory: searchCategoryStore.update,
    updateFilteredCategories: searchCategoryStore.updateFilteredCategories,
    generateApiCategoriesForView: searchCategoryStore.generateApiCategoriesForView,
    getSelectedTagsForCurrentTab: searchCategoryStore.getSelectedTagsForCurrentTab,
    setAllCategoriesFromInitialData: searchCategoryStore.setAllCategoriesFromInitialData,
    updateSelectedTagsByTab: searchCategoryStore.updateSelectedTagsByTab,
    updateCategoryTotalsMap: searchCategoryStore.updateCategoryTotalsMap,
    mergeCategoryTotals: searchCategoryStore.mergeCategoryTotals,
    updateTabCounts: searchCategoryStore.updateTabCounts,
    resetTabCounts: searchCategoryStore.resetTabCounts,
    setPendingTabCodes: searchCategoryStore.setPendingTabCodes,
    removePendingTabCode: searchCategoryStore.removePendingTabCode,
    clearPendingTabCodes: searchCategoryStore.clearPendingTabCodes,
    getTagCodesForTab: searchCategoryStore.getTagCodesForTab,
    getRequestTagCodesForView: searchCategoryStore.getRequestTagCodesForView,
    calculateTabCounts: searchCategoryStore.calculateTabCounts,

    // 결과 스토어
    setSearchResults: searchResultStore.setSearchResults,
    appendSearchResults: searchResultStore.appendSearchResults,
    clearSearchCache: searchResultStore.clearSearchCache,
    clearSearchResults: searchResultStore.clearSearchResults,
    constructSearchCacheKey: searchResultStore.constructSearchCacheKey,
    getFromSearchCache: searchResultStore.getFromSearchCache,
    processApiOrCacheResults: searchResultStore.processApiOrCacheResults,
    hasInSearchCache: searchResultStore.hasInSearchCache,

    // 뷰 스토어
    getView: searchViewStore.getState,
    setSelectedViewCode: searchViewStore.setSelectedViewCode,
    setCurrentPage: searchViewStore.setCurrentPage,

    // 상태 스토어
    getStatus: searchStatusStore.getState,
    setIsLoadingGlobal: searchStatusStore.setIsLoadingGlobal,
    setIsLoadingTabData: searchStatusStore.setIsLoadingTabData,
    setIsLoadingInitialMount: searchStatusStore.setIsLoadingInitialMount,
    withGlobalLoading: searchStatusStore.withGlobalLoading,

    // 검색 히스토리 스토어
    addOrUpdateHistory: searchHistoryStore.addOrUpdate,
    removeHistory: searchHistoryStore.remove,
};
